import * as React from "react";
import { useState } from "react";

import { darken } from "polished";
import styled from "styled-components";
import { OverlayTrigger } from "react-bootstrap";

import { ModuleSectionData, useModuleRefs } from "./ModuleSection";

import { ReactComponent as DocumentSvg } from "../assets/svg/newFile.svg";
import { ReactComponent as LinkSvg } from "../assets/svg/externalLink.svg";

interface ModuleFooterProps {
  moduleNumber: number;
  currentSection: number;
  totalSections: number;
  sectionTitle: string;
  sectionData?: ModuleSectionData[];
}

const popover = (
  <div
    className="bg-warning mb-0"
    css={`
      padding: 0.5rem 1rem 1rem;
      clip-path: polygon(
        0 0,
        100% 0,
        100% calc(100% - 0.5rem),
        calc(100% - 1rem) calc(100% - 0.5rem),
        calc(100% - 1.5rem) 100%,
        calc(100% - 2rem) calc(100% - 0.5rem),
        0 calc(100% - 0.5rem)
      );
    `}
  >
    <small
      className="text-white text-uppercase font-weight-bold"
      style={{ fontSize: "0.7rem" }}
    >
      click below to view resources
    </small>
  </div>
);

export const ModuleFooter: React.FC<ModuleFooterProps> = ({
  moduleNumber,
  currentSection,
  totalSections,
  sectionTitle,
  sectionData = [],
}) => {
  const { refList } = useModuleRefs();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  React.useEffect(() => {
    if (sectionData.length === 0 && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [sectionData.length]);

  return (
    <footer
      css={`
        position: sticky;
        bottom: 0;
        background-color: ${(p: any) => p.theme.info};
        z-index: 5;

        margin-left: -2rem;
        margin-right: -2rem;
        margin-bottom: -2rem;
      `}
      className="text-center text-white d-flex"
    >
      <div
        css={`
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${(p: any) => darken(0.075, p.theme.info)};
          padding: 0 1.5rem;
          clip-path: polygon(
            0 0,
            calc(100% - 0.5rem) 0,
            calc(100% - 0.5rem) calc(50% - 0.5rem),
            100% 50%,
            calc(100% - 0.5rem) calc(50% + 0.5rem),
            calc(100% - 0.5rem) 100%,
            0% 100%
          );
        `}
      >
        <strong className="h4 mb-0">Module {moduleNumber}</strong>
      </div>
      <FooterDescription className="d-none d-md-flex">
        <p className="mb-0 h4">{sectionTitle}</p>
      </FooterDescription>
      <FooterInfo className="">
        <p className="mb-0 p-3 h4">
          {currentSection} of {totalSections}
        </p>
        <OverlayTrigger
          show={sectionData.length !== 0}
          placement="top-end"
          overlay={popover}
        >
          <InfoButton
            clickable={sectionData.length !== 0}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            disabled={!sectionData.length}
          >
            <FooterIcon>
              <strong>i</strong>
            </FooterIcon>
          </InfoButton>
        </OverlayTrigger>
        <NextSection
          className="p-3"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            const nextSection = currentSection; // currentSection starts at 1, refList is an array (starts at 0)
            const sections = Array.from(refList.current.entries());
            sections[nextSection][0].scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }}
          disabled={currentSection === totalSections}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
            />
          </svg>
        </NextSection>
      </FooterInfo>

      {/* RESOURCES : TODO, look in to React.Portal/use Popper/react-bootstrap dropdown */}
      <ResourcesPopout show={isMenuOpen}>
        <CloseButton onClick={() => setIsMenuOpen(false)}>x</CloseButton>
        <h5 className="h2 text-center text-white p-3 mb-0">Resources</h5>
        <ul
          css={`
            text-align: left;
            & li {
              border-radius: 0;
            }
          `}
          className="list-group bg-info"
        >
          {sectionData.map(({ href, title, type }, idx) => (
            <li
              key={idx}
              className="list-group-item d-flex align-items-center bg-primary"
            >
              <span
                className="text-center"
                style={{ width: "35px", minWidth: "35px" }}
              >
                {(() => {
                  switch (type) {
                    case "document":
                      return (
                        <DocumentSvg fill="white" width={35} height={35} />
                      );
                    case "link":
                      return <LinkSvg fill="white" width={20} height={20} />;
                    default:
                      return null;
                  }
                })()}
              </span>
              <a
                href={href}
                className="d-inline-block stretched-link text-small text-white ml-3"
                style={{ fontSize: "0.9rem" }}
              >
                {title}
              </a>
            </li>
          ))}
        </ul>
      </ResourcesPopout>
    </footer>
  );
};

const FooterDescription = styled.div`
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const FooterInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  max-width: 300px;
`;

const InfoButton = styled.button<{ clickable: boolean }>`
  margin: 0;
  border: 0;
  height: 100%;
  padding: 0 1rem;
  transition: background-color 500ms cubic-bezier(0.34, 1.86, 0.64, 1);
  background-color: ${(p) => (p.clickable ? p.theme.primary : "transparent")};
  opacity: 0.4;
  &:not(:disabled) {
    cursor: pointer;
    opacity: 1;
  }
  &:focus {
    outline: none;
  }
`;

const FooterIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background-color: white;
  border-radius: 50%;
  strong {
    color: ${(p) => p.theme.info};
  }
`;

const NextSection = styled.button`
  margin: 0;
  background: 0;
  border: 0;
  color: #fff;
  &:focus {
    outline: none;
  }
  &:not(:disabled) {
    cursor: pointer;
  }
`;

const ResourcesPopout = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 100%;
  right: 0;
  width: 300px;
  display: ${(p) => (p.show ? "block" : "none")};
  background-color: ${(p) => p.theme.primary};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.1rem 0.5rem;
  font-size: 1.5rem;
  color: white;
  background-color: transparent;
  border: none;
  line-height: 1;
  cursor: pointer;
`;
